import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import {
  Plus, X, Loader2, Trash2, Lock, Layers, Sparkles, Gauge, Eye, LineChart,
  BarChart3, Trophy, Users, Store, Award, Camera, ShieldCheck, TrendingUp,
} from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const field = "w-full bg-[color:var(--charcoal)] border border-[color:var(--line)] rounded-xl px-4 py-3 text-white placeholder:text-[color:var(--chrome-3)] focus:outline-none focus:border-[color:var(--cyan)] transition-colors";

const CATEGORIES = [
  { id: "sports", label: "Sports" },
  { id: "pokemon", label: "Pokémon" },
  { id: "tcg", label: "Other TCG" },
  { id: "other", label: "Other" },
];

// Progressive-reveal teasers — locked for now, build curiosity.
const TEASERS = [
  { icon: Sparkles, title: "Card Rehab", copy: "Pro cleaning & restoration, requested in one tap." },
  { icon: Gauge, title: "CardScience Score", copy: "A smart condition & desirability signal." },
  { icon: Eye, title: "Watchlist", copy: "Follow cards you want — and ones you own." },
  { icon: LineChart, title: "Market Insights", copy: "See where values are heading." },
  { icon: BarChart3, title: "Collection Analytics", copy: "Understand your collection like never before." },
  { icon: Award, title: "Grading Opportunities", copy: "Know which cards are worth grading." },
  { icon: Trophy, title: "Achievements", copy: "Unlock milestones as your vault grows." },
  { icon: Users, title: "Community", copy: "Connect with serious collectors — privately." },
  { icon: Store, title: "Marketplace", copy: "Buy & sell, when you're ready. Coming soon." },
];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function PortfolioPanel({ cards }) {
  const { series, total, firstValue } = useMemo(() => {
    const valued = cards
      .filter((c) => Number(c.estimated_value) > 0 && c.created_at)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    let running = 0;
    const byDay = new Map(); // collapse same-day additions into one point (final cumulative)
    valued.forEach((c) => {
      running += Number(c.estimated_value) || 0;
      let label = "";
      try { label = format(parseISO(c.created_at), "MMM d"); } catch { label = ""; }
      byDay.set(label, running);
    });
    const totalVal = cards.reduce((s, c) => s + (Number(c.estimated_value) || 0), 0);
    const pts = Array.from(byDay.entries()).map(([date, value]) => ({ date, value }));
    const today = format(new Date(), "MMM d");
    // extend to "Today" only if the latest addition wasn't today
    if (pts.length > 0 && pts[pts.length - 1].date !== today) pts.push({ date: "Today", value: totalVal });
    // guarantee a visible rising line even when all cards were added the same day
    if (pts.length === 1) pts.unshift({ date: "Start", value: 0 });
    return { series: pts, total: totalVal, firstValue: pts.length ? pts[0].value : 0 };
  }, [cards]);

  const gain = total - firstValue;

  return (
    <div data-testid="portfolio-panel" id="portfolio" className="rounded-3xl holo-border holo-glow p-6 sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <p className="eyebrow mb-2 flex items-center gap-2"><TrendingUp size={13} className="text-[color:var(--cyan)]" /> Portfolio Value</p>
          <p data-testid="portfolio-total" className="h-display text-4xl sm:text-5xl text-holo leading-none">{money(total)}</p>
          <p className="text-sm text-[color:var(--chrome-2)] mt-2">
            {cards.length} card{cards.length !== 1 ? "s" : ""} tracked
            {series.length > 1 && gain > 0 && (
              <span className="text-[color:var(--cyan)]"> · +{money(gain)} since you started</span>
            )}
          </p>
        </div>
      </div>

      {series.length > 1 ? (
        <div data-testid="portfolio-chart" className="mt-6 h-48 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 8, right: 6, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="holoFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#24e0ff" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#9b5cff" stopOpacity={0.04} />
                </linearGradient>
                <linearGradient id="holoStroke" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#24e0ff" />
                  <stop offset="100%" stopColor="#9b5cff" />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false}
                tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} width={56} />
              <Tooltip
                contentStyle={{ background: "#0c0d10", border: "1px solid #2a2c33", borderRadius: 12, color: "#fff" }}
                labelStyle={{ color: "#9ca3af" }}
                formatter={(v) => [money(v), "Value"]} />
              <Area type="monotone" dataKey="value" stroke="url(#holoStroke)" strokeWidth={2.5} fill="url(#holoFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div data-testid="portfolio-chart-empty" className="mt-6 rounded-2xl border border-dashed border-[color:var(--line-2)] p-6 text-center">
          <LineChart size={22} className="text-[color:var(--chrome-3)] mx-auto mb-2" />
          <p className="text-sm text-[color:var(--chrome-3)]">Add cards with an estimated value to see your collection&apos;s value grow over time.</p>
        </div>
      )}
    </div>
  );
}

function AddCardModal({ onClose, onAdded }) {
  const [form, setForm] = useState({ name: "", year: "", set_name: "", category: "sports", estimated_value: "", notes: "" });
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(`${BACKEND_URL}${data.url}`);
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Card name is required."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/collection`, {
        ...form,
        estimated_value: form.estimated_value ? Number(String(form.estimated_value).replace(/[^0-9.]/g, "")) || null : null,
        image_url: image,
      });
      toast.success("Added to your vault");
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add card"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="add-card-modal" className="w-full max-w-lg rounded-3xl holo-border bg-black p-7 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="h-display text-2xl text-white">Add a card</h2>
          <button data-testid="add-card-close" onClick={onClose} className="h-8 w-8 rounded-full border border-[color:var(--line)] flex items-center justify-center text-[color:var(--chrome-3)] hover:text-white"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <input data-testid="card-name-input" value={form.name} onChange={(e) => up("name", e.target.value)} placeholder="Card / player name" className={field} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="card-year-input" value={form.year} onChange={(e) => up("year", e.target.value)} placeholder="Year (optional)" className={field} />
            <input value={form.set_name} onChange={(e) => up("set_name", e.target.value)} placeholder="Set (optional)" className={field} />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button key={c.id} type="button" data-testid={`cat-${c.id}`} onClick={() => up("category", c.id)}
                className={`rounded-full px-4 py-2 text-xs border transition-all ${form.category === c.id ? "holo-border text-white" : "border-[color:var(--line)] text-[color:var(--chrome-2)]"}`}>{c.label}</button>
            ))}
          </div>
          <input data-testid="card-value-input" value={form.estimated_value} onChange={(e) => up("estimated_value", e.target.value)} placeholder="Estimated value (optional)" className={field} />
          <textarea value={form.notes} onChange={(e) => up("notes", e.target.value)} placeholder="Notes (optional)" rows={2} className={field} />
          {image ? (
            <div className="relative h-32 w-32 rounded-xl overflow-hidden border border-[color:var(--line)]">
              <img src={image} alt="card" className="h-full w-full object-cover" />
              <button onClick={() => setImage(null)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center"><X size={12} /></button>
            </div>
          ) : (
            <label className="h-32 w-32 rounded-xl border border-dashed border-[color:var(--line-2)] flex flex-col items-center justify-center gap-1.5 cursor-pointer text-[color:var(--chrome-3)] hover:border-[color:var(--cyan)] hover:text-[color:var(--cyan)]">
              {uploading ? <Loader2 size={20} className="animate-spin" /> : <Camera size={20} />}
              <span className="text-[10px] font-tech tracking-wide">{uploading ? "UPLOADING" : "ADD PHOTO"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
            </label>
          )}
        </div>
        <button data-testid="card-save" onClick={save} disabled={busy} className="btn-holo w-full rounded-xl py-3.5 text-sm mt-5 inline-flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <><Loader2 size={16} className="animate-spin" /> ADDING…</> : <><Plus size={16} /> ADD TO MY VAULT</>}
        </button>
      </div>
    </div>
  );
}

export default function Vault() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(params.get("add") === "1");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/collection`); setCards(data.items || []); }
    catch { /* handled by auth redirect */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeAdd = () => { setShowAdd(false); if (params.get("add")) { params.delete("add"); setParams(params, { replace: true }); } };

  const remove = async (id) => {
    try { await axios.delete(`${API}/collection/${id}`); setCards((c) => c.filter((x) => x.id !== id)); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  const totalValue = cards.reduce((s, c) => s + (Number(c.estimated_value) || 0), 0);
  const handle = user?.collector_handle || user?.username || "Collector";

  return (
    <div className="bg-ink min-h-screen pb-24">
      {showAdd && <AddCardModal onClose={closeAdd} onAdded={() => { closeAdd(); load(); }} />}

      <div className="mx-auto max-w-[1100px] px-5 pt-14">
        <p className="eyebrow mb-2 flex items-center gap-2"><Lock size={13} className="text-[color:var(--cyan)]" /> Your private collection</p>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="h-display text-4xl sm:text-5xl text-holo">THE VAULT</h1>
            <p className="text-sm text-[color:var(--chrome-2)] mt-2">Welcome back, {handle}. {cards.length} card{cards.length !== 1 ? "s" : ""} · est. {money(totalValue)}</p>
          </div>
          <button data-testid="vault-add-card" onClick={() => setShowAdd(true)} className="btn-holo inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm">
            <Plus size={16} /> ADD A CARD
          </button>
        </div>

        {/* portfolio value + chart */}
        {!loading && cards.length > 0 && (
          <div className="mt-8">
            <PortfolioPanel cards={cards} />
          </div>
        )}

        {/* collection grid */}
        <div className="mt-8">
          {loading ? (
            <div className="flex items-center gap-2 text-[color:var(--chrome-3)] py-10"><Loader2 size={18} className="animate-spin" /> Loading your vault…</div>
          ) : cards.length === 0 ? (
            <div data-testid="vault-empty" className="rounded-3xl holo-border p-10 text-center">
              <Layers size={30} className="text-holo mx-auto mb-3" />
              <p className="text-white font-semibold">Your vault is empty.</p>
              <p className="text-sm text-[color:var(--chrome-3)] mt-2 max-w-md mx-auto">Add your first card to start building your private portfolio. Only you can see it.</p>
              <button data-testid="vault-empty-add" onClick={() => setShowAdd(true)} className="btn-holo inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm mt-5"><Plus size={16} /> ADD YOUR FIRST CARD</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="vault-grid">
              {cards.map((c) => (
                <div key={c.id} data-testid={`vault-card-${c.id}`} className="group rounded-2xl border border-[color:var(--line)] overflow-hidden bg-black">
                  <div className="aspect-[3/4] bg-[color:var(--charcoal)] relative">
                    {c.image_url ? <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[color:var(--chrome-3)]"><Layers size={28} /></div>}
                    <button onClick={() => remove(c.id)} data-testid={`vault-remove-${c.id}`} className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
                  </div>
                  <div className="p-3">
                    <p className="text-sm text-white font-semibold truncate">{c.name}</p>
                    <p className="text-xs text-[color:var(--chrome-3)] mt-0.5 truncate">{[c.year, c.set_name].filter(Boolean).join(" · ") || "—"}</p>
                    {c.estimated_value ? <p className="text-xs text-[color:var(--cyan)] mt-1">{money(c.estimated_value)}</p> : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* progressive-reveal teasers */}
        <div className="mt-16">
          <p className="eyebrow mb-2">Unlocking soon</p>
          <h2 className="h-display text-2xl text-chrome">THERE&apos;S MORE BEHIND THE DOOR.</h2>
          <p className="text-sm text-[color:var(--chrome-3)] mt-2 max-w-xl">As your vault grows, CardScience HQ reveals new tools built for serious collectors.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {TEASERS.map((t) => (
              <div key={t.title} data-testid={`teaser-${t.title.toLowerCase().replace(/\s+/g, "-")}`} className="relative rounded-2xl border border-[color:var(--line)] glass p-5 overflow-hidden">
                <div className="absolute top-3 right-3 text-[color:var(--chrome-3)]"><Lock size={14} /></div>
                <div className="h-10 w-10 rounded-lg border border-[color:var(--line-2)] flex items-center justify-center text-[color:var(--cyan)] mb-3"><t.icon size={18} /></div>
                <p className="text-white font-semibold text-sm">{t.title}</p>
                <p className="text-xs text-[color:var(--chrome-2)] mt-1 leading-relaxed">{t.copy}</p>
                <span className="inline-block mt-3 text-[10px] font-tech tracking-widest text-[color:var(--chrome-3)]">COMING SOON</span>
              </div>
            ))}
          </div>
        </div>

        {/* mail-in service entry (kept, behind the wall) */}
        <div className="mt-14 rounded-3xl holo-border holo-glow p-7 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <ShieldCheck size={22} className="text-[color:var(--cyan)] shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-semibold">Want a card professionally cleaned?</p>
              <p className="text-sm text-[color:var(--chrome-2)] mt-1">Send it to the lab for Clean &amp; Polish, Rehab or grading prep.</p>
            </div>
          </div>
          <Link to="/submit" data-testid="vault-submit-link" className="btn-holo inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm">OPEN THE CARD LAB</Link>
        </div>
      </div>
    </div>
  );
}
